import { useLingui } from '@lingui/react/macro'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { CopyIcon, OpenInNewWindowIcon } from '@radix-ui/react-icons'
import { Provider } from '@radix-ui/react-tooltip'
import { Tooltip } from '@radix-ui/themes'
import React from 'react'
import styled from 'styled-components'

const StyledContainer = styled.div`
  width: 100%;
  display: flex;
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.1875rem;
  padding: 0.5rem 1rem;
  justify-content: space-between;
  align-items: center;
`

const StyledSupportButton = styled.div`
  a {
    color: ${({ theme }) => theme.forecast.secondaryText};
  }
  a:hover {
    color: ${({ theme }) => theme.forecast.activeText};
  }
  height: 100%;
`

const StyledLink = styled.a`
  text-decoration: inherit;
  display: flex;
  align-items: center;
`

const StyledSubText = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 11px;
`

export const StyledOpenInNewWindowIcon = styled(OpenInNewWindowIcon)`
  margin-left: 5px;
`

const StyledCopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  margin: 0;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
  border-radius: var(--radius, 4px);

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.forecast.activeText};
    outline-offset: 2px;
  }
`

const SUPPORT_EMAIL_ADDRESS = 'support@geckorobotics.com'

/**
 * Renders the mailto link and copy-to-clipboard control as real
 * DropdownMenu.Items (via asChild) so each participates in the menu's
 * arrow-key roving focus. Radix menus disable Tab, so loose links/buttons
 * would otherwise be unreachable by keyboard.
 */
export const EmailSupport: React.FC = () => {
  const { t } = useLingui()
  const copyAddress = () => {
    navigator.clipboard.writeText(SUPPORT_EMAIL_ADDRESS)
  }

  return (
    <StyledContainer>
      <StyledSupportButton>
        <DropdownMenu.Item asChild>
          <StyledLink href={`mailto:${SUPPORT_EMAIL_ADDRESS}`}>
            {t`Email Support`}
            <StyledOpenInNewWindowIcon />
          </StyledLink>
        </DropdownMenu.Item>
        <StyledSubText>{SUPPORT_EMAIL_ADDRESS}</StyledSubText>
      </StyledSupportButton>
      <Provider>
        <Tooltip
          content={t`Copy Email Address`}
          side={'left'}
          style={{ minHeight: 'auto' }}>
          <DropdownMenu.Item
            asChild
            onSelect={(event) => {
              event.preventDefault()
              copyAddress()
            }}>
            <StyledCopyButton type={'button'} aria-label={t`Copy to Clipboard`}>
              <CopyIcon />
            </StyledCopyButton>
          </DropdownMenu.Item>
        </Tooltip>
      </Provider>
    </StyledContainer>
  )
}
